"use client"

import clsx from "clsx"
import React, { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils"

/**
 * @description 단일 체크박스
 * @example
 * <TCheck checked={isAgreed} label="이용약관 동의" onChange={setIsAgreed} />
 */
interface TCheckProps extends BaseInteractiveProps {
	/** 체크박스 name 속성 */
	name?: string
	/**
	 * 현재 체크 상태
	 * @default false
	 */
	checked?: boolean
	/** 체크박스 값 (폼 제출 시 사용) */
	value?: string | number
	/** 체크박스 옆에 표시할 라벨 */
	label?: string | React.ReactNode
	/** 라벨 아래 보조 설명 텍스트 */
	helper?: string | React.ReactNode
	/**
	 * 부분 선택(indeterminate) 상태. 하위 항목 일부만 선택되었을 때 사용
	 * @ai-style true=대시(-) 아이콘 표시, 클릭 시 전체 선택으로 전환
	 * @default false
	 */
	indeterminate?: boolean
	/** 체크 상태 변경 콜백. boolean 값 전달 */
	onChange?: (checked: boolean) => void
}

export const TCheck = React.memo(
	React.forwardRef<HTMLInputElement, TCheckProps>((props, ref) => {
		const { id, name, checked = false, value, label, helper, disabled = false, indeterminate = false, className, dataAttributes, onChange, ...restProps } = props
		const dataProps = useDataAttributes(dataAttributes)

		const [updateChecked, setUpdateChecked] = useState<boolean>(checked)
		const dataState = indeterminate ? "indeterminate" : updateChecked ? "checked" : "unchecked"

		useEffect(() => {
			setUpdateChecked(checked)
		}, [checked])

		const onChangeValue = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				const newChecked = event.target.checked
				if (indeterminate && onChange) {
					onChange(true)
					setUpdateChecked(true)
				} else {
					setUpdateChecked(newChecked)
					if (onChange) {
						onChange(newChecked)
					}
				}
			},
			[onChange, indeterminate]
		)

		const checkboxInput = (
			<input
				ref={ref}
				id={id}
				name={name}
				type="checkbox"
				checked={updateChecked}
				disabled={disabled}
				value={value}
				{...dataProps}
				onChange={onChangeValue}
				aria-describedby={helper && id ? `${id}-helper` : undefined}
			/>
		)

		return (
			<div className={clsx("TCheck", className)} {...restProps} data-disabled={disabled}>
				<label htmlFor={id}>
					{checkboxInput}
					<span className="icon" data-state={dataState} aria-hidden="true">
						{dataState === "checked" && <CheckIcon />}
						{dataState === "indeterminate" && <IndeterminateIcon />}
					</span>
					{label && <div className="label">{label}</div>}
				</label>
				{helper && label && (
					<span id={id ? `${id}-helper` : undefined} className="helper">
						{helper}
					</span>
				)}
			</div>
		)
	})
)

TCheck.displayName = "TCheck"

const CheckIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M12.4983 4.43945C12.8079 4.71464 12.8357 5.18869 12.5606 5.49828L7.67167 10.9983C7.53723 11.1495 7.34738 11.24 7.14523 11.2492C6.94308 11.2584 6.74579 11.1856 6.59816 11.0472L3.48705 8.13049C3.18486 7.8472 3.16955 7.37257 3.45285 7.07039C3.73615 6.7682 4.21077 6.75289 4.51296 7.03619L7.06222 9.42612L11.4394 4.50174C11.7146 4.19215 12.1887 4.16426 12.4983 4.43945Z"
			fill="white"
		/>
	</svg>
)

const IndeterminateIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M3.25 8C3.25 7.58579 3.58579 7.25 4 7.25H12C12.4142 7.25 12.75 7.58579 12.75 8C12.75 8.41421 12.4142 8.75 12 8.75H4C3.58579 8.75 3.25 8.41421 3.25 8Z"
			fill="white"
		/>
	</svg>
)
